import { useState, useEffect } from 'react'
import BundleExperienceField, { type BundleExperienceValue } from '../components/BundleExperienceField'
import { uxSummaryFromConfig } from '../utils/templateSummary'
import { useParams, useNavigate } from 'react-router-dom'
import { tenantAPI } from '../services/api'
import { errorText } from '../utils/errorText'
import CommandBlock from '../components/CommandBlock'
import { ConfirmDialog } from '../components/ui'

interface BundleItem {
  id: string
  app_id: string
  app_name: string
  action: string
  order_index: number
}

export default function BundleDetail() {
  const { bundleId } = useParams<{ bundleId: string }>()
  const navigate = useNavigate()
  const [bundle, setBundle] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showAddApp, setShowAddApp] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [addAppId, setAddAppId] = useState('')
  const [addAction, setAddAction] = useState('install')
  const [catalogApps, setCatalogApps] = useState<any[]>([])
  const [catalogSearch, setCatalogSearch] = useState('')
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [showGenerate, setShowGenerate] = useState(false)
  const [tokenExpiry, setTokenExpiry] = useState(24)
  const [tokenMaxUses, setTokenMaxUses] = useState<number | null>(null)
  const [generatedCommand, setGeneratedCommand] = useState('')
  const [generatedOneliner, setGeneratedOneliner] = useState('')
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [editExperience, setEditExperience] = useState<BundleExperienceValue>({
    templateId: null,
    overrides: null,
  })

  const fetchBundle = async () => {
    if (!bundleId) return
    try {
      const { data } = await tenantAPI.get(`/bundles/${bundleId}`)
      setBundle(data)
      setEditName(data.name)
      setEditDesc(data.description || '')
      setEditExperience({
        templateId: data.ux_template_id ?? null,
        overrides: data.ux_overrides_json ?? null,
      })
    } catch (err) {
      console.error('Failed to fetch bundle:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchBundle() }, [bundleId])

  const handleAddApp = async () => {
    if (!addAppId.trim() || !bundleId) return
    try {
      await tenantAPI.post(`/bundles/${bundleId}/items`, {
        app_id: addAppId.trim(),
        action: addAction,
      })
      setShowAddApp(false)
      setAddAppId('')
      fetchBundle()
    } catch (err: any) {
      alert(errorText(err, 'Failed to add app'))
    }
  }

  const searchCatalog = async () => {
    setCatalogLoading(true)
    try {
      const { data } = await tenantAPI.get('/apps', { params: { search: catalogSearch || undefined, limit: 20 } })
      setCatalogApps(data.apps || [])
    } catch (err) {
      console.error('Failed to search catalog:', err)
    } finally {
      setCatalogLoading(false)
    }
  }

  const handleAddFromCatalog = async (app: any, action: string) => {
    if (!bundleId) return
    try {
      await tenantAPI.post(`/bundles/${bundleId}/items`, {
        app_id: app.id,
        action,
      })
      fetchBundle()
    } catch (err: any) {
      alert(errorText(err, 'Failed to add app'))
    }
  }

  const handleRemoveItem = async (itemId: string) => {
    if (!bundleId) return
    try {
      await tenantAPI.delete(`/bundles/${bundleId}/items/${itemId}`)
      fetchBundle()
    } catch (err) {
      console.error('Failed to remove item:', err)
    }
  }

  const handleGenerateToken = async () => {
    if (!bundleId) return
    try {
      const { data } = await tenantAPI.post(`/bundles/${bundleId}/generate-token`, {
        expiry_hours: tokenExpiry,
        max_uses: tokenMaxUses,
      })
      setGeneratedCommand(data.cli_command)
      setGeneratedOneliner(data.oneliner)
    } catch (err: any) {
      alert(errorText(err, 'Failed to generate token'))
    }
  }

  const handleSaveEdit = async () => {
    if (!bundleId) return
    try {
      // ux_template_id is sent every time, including as null: omitting it means "leave it alone",
      // which would make clearing the selection impossible from this screen.
      await tenantAPI.put(`/bundles/${bundleId}`, {
        name: editName,
        description: editDesc || null,
        ux_template_id: editExperience.templateId,
        ux_overrides_json: editExperience.overrides,
      })
      setEditing(false)
      fetchBundle()
    } catch (err: any) {
      alert(errorText(err, 'Failed to update'))
    }
  }

  const handleDelete = async () => {
    if (!bundleId) return
    try {
      await tenantAPI.delete(`/bundles/${bundleId}`)
      navigate('/bundles')
    } catch (err) {
      alert(errorText(err, 'Failed to delete the bundle'))
    }
  }

  if (loading || !bundle) return <div className="p-8 text-theme-text-secondary">Loading...</div>

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/bundles')} className="text-theme-text-tertiary hover:text-theme-text-primary text-sm mb-3 inline-flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back to Bundles
        </button>

        {editing ? (
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-5">
            <h2 className="text-sm font-medium text-theme-text-secondary mb-3">Edit Bundle</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-theme-text-tertiary mb-1">Name</label>
                <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                  className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-theme-text-tertiary mb-1">Description</label>
                <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={2}
                  placeholder="What is this bundle for?"
                  className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-theme-text-tertiary mb-1">Deployment experience</label>
                <BundleExperienceField value={editExperience} onChange={setEditExperience} />
              </div>
              <div className="flex gap-2">
                <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Save Changes</button>
                <button onClick={() => setEditing(false)} className="px-4 py-2 text-theme-text-tertiary hover:text-theme-text-primary rounded-lg text-sm">Cancel</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-theme-text-primary">{bundle.name}</h1>
                {bundle.description && <p className="text-theme-text-secondary text-sm mt-0.5">{bundle.description}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 px-3 py-2 border border-theme-border-primary text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-tertiary rounded-lg text-sm transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                Edit
              </button>
              <button onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1 px-3 py-2 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Generate CLI Modal */}
      {showGenerate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-lg font-semibold text-theme-text-primary mb-4">Generate CLI Command</h2>

            {!generatedCommand ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-theme-text-secondary mb-1">Token Expiry</label>
                  <select value={tokenExpiry} onChange={e => setTokenExpiry(parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary text-sm">
                    <option value={1}>1 hour</option>
                    <option value={24}>24 hours</option>
                    <option value={168}>7 days</option>
                    <option value={720}>30 days</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-theme-text-secondary mb-1">Max Uses (leave empty for unlimited)</label>
                  <input type="number" value={tokenMaxUses || ''} min={1}
                    onChange={e => setTokenMaxUses(e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Unlimited"
                    className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary text-sm" />
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setShowGenerate(false)} className="px-4 py-2 text-theme-text-tertiary text-sm">Cancel</button>
                  <button onClick={handleGenerateToken}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm">Generate</button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* One-liner (primary) */}
                <div>
                  <label className="block text-sm font-medium text-theme-text-primary mb-2">
                    Single PowerShell Command (recommended)
                  </label>
                  <p className="text-xs text-theme-text-tertiary mb-2">
                    Downloads the agent, runs the bundle, and cleans up — all in one command. No pre-install needed.
                  </p>
                  {/* Summarised, not printed: the one-liner is a download URL and a JWT, and a
                      wall of base64 in a dialog is read by nobody. Show opens it whole. */}
                  <CommandBlock
                    command={generatedOneliner}
                    summary="PowerShell, elevated — downloads the agent, runs the bundle, removes it"
                    copyLabel="Copy one-liner"
                  />
                </div>

                {/* Two-step (if agent already downloaded) */}
                <div>
                  <label className="block text-sm text-theme-text-secondary mb-2">
                    Or if agent is already downloaded:
                  </label>
                  <CommandBlock command={generatedCommand} copyLabel="Copy CLI command" />
                </div>

                <div className="flex justify-end gap-3">
                  <button onClick={() => { setGeneratedCommand(''); setGeneratedOneliner(''); setShowGenerate(false); }}
                    className="px-4 py-2 text-theme-text-tertiary text-sm">Close</button>
                  <button onClick={() => { setGeneratedCommand(''); setGeneratedOneliner('') }}
                    className="px-4 py-2 bg-theme-tertiary text-theme-text-primary rounded-lg text-sm">Generate Another</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary cards */}
      {(bundle.items || []).length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-theme-text-primary">{(bundle.items || []).length}</div>
            <div className="text-xs text-theme-text-tertiary mt-1">Total Packages</div>
          </div>
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {(bundle.items || []).filter((i: BundleItem) => i.action === 'install').length}
            </div>
            <div className="text-xs text-theme-text-tertiary mt-1">Install or update</div>
          </div>
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {(bundle.items || []).filter((i: BundleItem) => i.action === 'update').length}
            </div>
            <div className="text-xs text-theme-text-tertiary mt-1">Update only</div>
          </div>
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {(bundle.items || []).filter((i: BundleItem) => i.action === 'uninstall').length}
            </div>
            <div className="text-xs text-theme-text-tertiary mt-1">Uninstall</div>
          </div>
        </div>
      )}

      {/* What the machine will see. Named on the page, not only inside the editor: a bundle that
          prompts and one that installs silently otherwise look identical. */}
      <div className="bg-theme-card border border-theme-border-primary rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <div>
            <div className="text-xs text-theme-text-tertiary">Deployment experience</div>
            <div className="text-sm text-theme-text-primary mt-0.5">
              {bundle.ux_template_name || 'Silent — no prompts, no branding'}
            </div>
            {bundle.ux_config && (
              <div className="text-xs text-theme-text-tertiary mt-1">
                {uxSummaryFromConfig(bundle.ux_config)}
              </div>
            )}
          </div>
          {bundle.ux_overrides_json && (
            <span className="ml-auto text-xs text-blue-600 dark:text-blue-400 shrink-0">
              adjusted for this bundle
            </span>
          )}
        </div>
      </div>

      {/* Deployment Methods */}
      {(bundle.items || []).length > 0 && !showGenerate && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Self-Service CLI Deployment */}
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-theme-text-primary">Self-Service CLI Deployment</h3>
                <p className="text-xs text-theme-text-tertiary">No MDM required</p>
              </div>
            </div>
            <div className="space-y-2 text-xs text-theme-text-secondary">
              <p>Generate a single PowerShell one-liner command that can be run on any Windows machine:</p>
              <ol className="list-decimal list-inside space-y-1 text-theme-text-tertiary">
                <li>Click <strong className="text-theme-text-primary">Generate CLI</strong> and set token expiry</li>
                <li>Copy the one-liner and share it (email, chat, script)</li>
                <li>Recipient opens <strong className="text-theme-text-primary">elevated PowerShell</strong> (Run as Admin)</li>
                <li>Pastes and runs — agent downloads, installs/uninstalls packages, cleans up</li>
              </ol>
              <p className="text-theme-text-tertiary mt-2">Token is time-limited and optionally use-limited for security.</p>
            </div>
            <button onClick={() => setShowGenerate(true)}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              Generate CLI Command
            </button>
          </div>

          {/* SCCM Deployment */}
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-theme-text-primary">SCCM / MECM Deployment</h3>
                <p className="text-xs text-theme-text-tertiary">Microsoft Endpoint Configuration Manager</p>
              </div>
            </div>
            <div className="space-y-2 text-xs text-theme-text-secondary">
              <p>Export a zip package for import into your SCCM infrastructure:</p>
              <ol className="list-decimal list-inside space-y-1 text-theme-text-tertiary">
                <li>Click <strong className="text-theme-text-primary">Export for SCCM</strong> to download the zip</li>
                <li>Copy the zip to your <strong className="text-theme-text-primary">SCCM site server</strong></li>
                <li>Run the included <code className="bg-theme-tertiary px-1 rounded">Import-SCCMApplications.ps1</code> script</li>
                <li>Script creates SCCM Applications with PSADT deployment types</li>
                <li>Distribute content + deploy to collections as usual</li>
              </ol>
              <p className="text-theme-text-tertiary mt-2">Includes PSADT packages, detection rules, and install/uninstall commands.</p>
            </div>
            <button
              onClick={async () => {
                try {
                  const response = await tenantAPI.get(`/bundles/${bundleId}/export-sccm`, { responseType: 'blob' })
                  const url = window.URL.createObjectURL(new Blob([response.data]))
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `${bundle.name}-SCCM-Export.zip`
                  document.body.appendChild(a)
                  a.click()
                  window.URL.revokeObjectURL(url)
                  document.body.removeChild(a)
                } catch (err: any) {
                  alert(errorText(err, 'Failed to export'))
                }
              }}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Export for SCCM
            </button>
          </div>
        </div>
      )}

      {/* Items table */}
      <div className="bg-theme-card border border-theme-border-primary rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-theme-border-primary">
          <h2 className="text-sm font-medium text-theme-text-secondary">Packages ({(bundle.items || []).length})</h2>
          <button onClick={() => setShowAddApp(!showAddApp)}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium">
            + Add Package
          </button>
        </div>

        {showAddApp && (
          <div className="p-4 border-b border-theme-border-primary bg-theme-tertiary">
            {/* Catalog search */}
            <div className="flex gap-2 mb-3">
              <input type="text" value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchCatalog()}
                placeholder="Search the catalog..."
                className="flex-1 px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary text-sm" />
              <button onClick={searchCatalog}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
                Search
              </button>
            </div>

            {/* Or manual ID entry */}
            <div className="flex gap-2 mb-3 items-center">
              <span className="text-xs text-theme-text-tertiary">Or enter ID directly:</span>
              <input type="text" value={addAppId} onChange={e => setAddAppId(e.target.value)}
                placeholder="tenant-xxx-appname"
                className="flex-1 px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary text-sm" />
              <select value={addAction} onChange={e => setAddAction(e.target.value)}
                className="px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary text-sm">
                <option value="install">Install or update</option>
                <option value="update">Update only</option>
                <option value="uninstall">Uninstall</option>
              </select>
              <button onClick={handleAddApp} disabled={!addAppId.trim()}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm disabled:opacity-50">Add</button>
            </div>

            {/* Catalog results */}
            {catalogLoading && <p className="text-sm text-theme-text-tertiary">Searching...</p>}
            {catalogApps.length > 0 && (
              <div className="max-h-60 overflow-y-auto border border-theme-border-primary rounded-lg divide-y divide-theme-border-primary">
                {catalogApps.map(app => {
                  const alreadyAdded = (bundle?.items || []).some((i: any) => i.app_id === app.id)
                  return (
                    <div key={app.id} className="flex items-center justify-between px-3 py-2 hover:bg-theme-secondary/50">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-theme-text-primary truncate">{app.display_name || app.name}</div>
                        <div className="text-xs text-theme-text-tertiary">{app.publisher} &middot; v{app.current_version?.version || '?'}</div>
                      </div>
                      {alreadyAdded ? (
                        <span className="text-xs text-theme-text-tertiary px-2">Added</span>
                      ) : (
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => handleAddFromCatalog(app, 'install')}
                            className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs">+ Install or update</button>
                          <button onClick={() => handleAddFromCatalog(app, 'update')}
                            className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs">+ Update only</button>
                          <button onClick={() => handleAddFromCatalog(app, 'uninstall')}
                            className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs">+ Uninstall</button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        <table className="w-full">
          <thead className="bg-theme-tertiary">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-theme-text-secondary uppercase">#</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-theme-text-secondary uppercase">Package</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-theme-text-secondary uppercase">Source</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-theme-text-secondary uppercase">Action</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-theme-text-secondary uppercase">Remove</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-theme-border-primary">
            {(bundle.items || []).length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-theme-text-tertiary">
                No packages in this bundle yet. Click "Add Package" to search the catalog.
              </td></tr>
            ) : (bundle.items as BundleItem[]).map((item, i) => {
              // Show display name, fallback to app_id if name is a UUID or missing
              const isUUID = /^[0-9a-f]{8}-/.test(item.app_name || '')
              const displayName = (!item.app_name || isUUID) ? (item.app_id.split('-').slice(-1)[0] || item.app_id) : item.app_name
              return (
              <tr key={item.id}>
                <td className="px-4 py-3 text-xs text-theme-text-tertiary">{i + 1}</td>
                <td className="px-4 py-3">
                  <a href={`/apps/${item.app_id}`} onClick={(e) => { e.preventDefault(); navigate(`/apps/${item.app_id}`) }}
                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium">
                    {displayName}
                  </a>
                  <div className="text-xs text-theme-text-tertiary">{item.app_id.length > 20 ? item.app_id.slice(0, 8) + '...' : item.app_id}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                    /^[0-9a-f]{8}-/.test(item.app_id)
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {/^[0-9a-f]{8}-/.test(item.app_id) ? 'Public Catalog' : 'Private'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {/* Three actions, so a select rather than a toggle. */}
                  <select
                    value={item.action}
                    aria-label={`Action for ${item.app_name}`}
                    onChange={async (e) => {
                      try {
                        await tenantAPI.patch(`/bundles/${bundleId}/items/${item.id}?action=${e.target.value}`)
                        fetchBundle()
                      } catch (err: any) {
                        alert(errorText(err, 'Failed to update'))
                      }
                    }}
                    className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer border-0 ${
                      item.action === 'install'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : item.action === 'update'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                    }`}
                  >
                    <option value="install">Install or update</option>
                    <option value="update">Update only</option>
                    <option value="uninstall">Uninstall</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => handleRemoveItem(item.id)}
                    className="text-red-500 hover:text-red-400 text-xs">Remove</button>
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete bundle"
        message={`Delete the bundle "${bundle.name}"? The generated CLI command will stop working.`}
        confirmLabel="Delete bundle"
      />
    </div>
  )
}
